import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import Layout from "../../layout/Layout";
import Switch from "react-switch";
import Fancybox from "../../Component/FancyBox";
import { API } from "../../App";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import TagsInput from "../../Component/TagsInput";
import ImageInput from "../../Component/theme-management/ImageInput";
import {
  buildWallpaperErrors,
  isWallpaperFormValid,
} from "../../helpers/wallpaperValidation";

const WallpaperEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(true);
  const [premium, setPremium] = useState(false);
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [wallpaperFile, setWallpaperFile] = useState(null);
  const [wallpaperIpadFile, setWallpaperIpadFile] = useState(null);
  const [fullImageFile, setFullImageFile] = useState(null);
  const [fullImageIpadFile, setFullImageIpadFile] = useState(null);

  const [user, setUser] = useState({
    theme_id: "",
    wallpaper_name: "",
    resolution: "",
    resolution_ipad: "",
    category: "",
    tags: [],
    thumbnail_url: "",
    wallpaper_url: "",
    wallpaper_url_ipad: "",
    full_image: "",
    full_image_ipad: "",
  });

  const [touched, setTouched] = useState({});

  // Edit allows keeping the existing image, so a required image is satisfied
  // by either a newly chosen file or the existing url.
  const errors = useMemo(
    () =>
      buildWallpaperErrors(
        {
          ...user,
          thumbnail: thumbnailFile || user.thumbnail_url,
          wallpaper: wallpaperFile || user.wallpaper_url,
        },
        { requireFiles: true }
      ),
    [user, thumbnailFile, wallpaperFile]
  );

  const showError = (field) => touched[field] && errors[field];

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const fetchThemes = async () => {
    try {
      const token = Cookies.get("jwt");
      const res = await API.post(
        "/dashboardThemesData",
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      setThemes(res.data.data || []);
    } catch (error) {
      console.error("Error fetching themes:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    const token = Cookies.get("jwt");
    const res = await API.post(
      `/wallpapers/${id}`,
      {},
      { headers: { authorization: `Bearer ${token}` } }
    );
    const data = res.data.data;
    setUser({
      theme_id: data.theme_id?._id || data.theme_id || "",
      wallpaper_name: data.wallpaper_name,
      resolution: data.resolution || "",
      resolution_ipad: data.resolution_ipad || "",
      category: data.category?._id || data.category || "",
      tags: Array.isArray(data.tags)
        ? data.tags
        : data.tags
        ? String(data.tags)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
      thumbnail_url: data.thumbnail_url,
      wallpaper_url: data.wallpaper_url,
      wallpaper_url_ipad: data.wallpaper_url_ipad,
      full_image: data.full_image || "",
      full_image_ipad: data.full_image_ipad || "",
    });
    setStatus(data.status);
    setPremium(data.premium);
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await API.post("/widget_category");
      setCategories(res.data.data[0]?.get_SubCategory || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchThemes();
    fetchCategories();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmission = async () => {
    setTouched({
      theme_id: true,
      wallpaper_name: true,
      resolution: true,
      resolution_ipad: true,
      thumbnail: true,
      wallpaper: true,
    });

    if (!isWallpaperFormValid(errors)) {
      toast.error("Please fix the highlighted fields.", {
        position: "bottom-right",
        autoClose: 1500,
      });
      return;
    }

    const formdata = new FormData();
    formdata.append("theme_id", user.theme_id);
    formdata.append("wallpaper_name", user.wallpaper_name.trim());
    if (thumbnailFile) formdata.append("thumbnail_url", thumbnailFile);
    if (wallpaperFile) formdata.append("wallpaper_url", wallpaperFile);
    if (wallpaperIpadFile)
      formdata.append("wallpaper_url_ipad", wallpaperIpadFile);
    if (fullImageFile) formdata.append("full_image", fullImageFile);
    if (fullImageIpadFile)
      formdata.append("full_image_ipad", fullImageIpadFile);
    formdata.append("resolution", user.resolution.trim());
    formdata.append("resolution_ipad", user.resolution_ipad.trim());
    formdata.append("category", user.category);
    formdata.append("tags", user.tags.join(","));
    formdata.append("premium", premium);
    formdata.append("status", status);

    setLoading(true);
    const token = Cookies.get("jwt");
    const res = await API.post(`/wallpapersUpdate/${id}`, formdata, {
      headers: { authorization: `Bearer ${token}` },
    });
    setLoading(false);
    if (res.data.status === true) {
      navigate("/wallpaper");
      toast.success("Data Update Sucessfully", {
        position: "bottom-right",
        autoClose: 500,
      });
    } else {
      toast.error(res.data.response_message || "Something went wrong!");
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1"> Edit Wallpaper</h3>
      </div>
      <div className="page-content">
        <Form noValidate>
          <Row>
            <Col xs={12}>
              <Card className="mb-4">
                <Card.Body>
                  {loading ? (
                    <div className="loader table-loader"></div>
                  ) : (
                    <></>
                  )}
                  <Row>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="theme_id">Theme</Form.Label>
                        <Form.Select
                          id="theme_id"
                          className="my-2"
                          name="theme_id"
                          value={user.theme_id}
                          onChange={onInputChange}
                        >
                          <option value="">Select Theme (Optional)</option>
                          {themes.map((val) => (
                            <option key={val._id} value={val._id}>
                              {val.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="wallpaper_name">
                          Name
                          <i className="bx bxs-asterisk text-danger ms-1" />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="wallpaper_name"
                          className="my-2"
                          name="wallpaper_name"
                          value={user.wallpaper_name}
                          onChange={onInputChange}
                          onBlur={() => markTouched("wallpaper_name")}
                          isInvalid={!!showError("wallpaper_name")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.wallpaper_name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="category">Category</Form.Label>
                        <Form.Select
                          id="category"
                          className="my-2"
                          name="category"
                          value={user.category}
                          onChange={onInputChange}
                        >
                          <option value="">Select Category</option>
                          {categories.map((val) => (
                            <option key={val._id} value={val._id}>
                              {val.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="thumbnail_url">
                          Thumbnail Image
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id="thumbnail_url"
                          name="thumbnail_url"
                          className="my-2"
                          accept="image/*"
                          onChange={(e) =>
                            setThumbnailFile(e.target.files[0] || null)
                          }
                        />
                        <Fancybox>
                          {user.thumbnail_url ? (
                            <a
                              data-fancybox="thumb-image"
                              href={user.thumbnail_url}
                            >
                              <img
                                src={user.thumbnail_url}
                                className="hv-40 bg-light"
                                alt="Thumbnail"
                              />
                            </a>
                          ) : (
                            ""
                          )}
                        </Fancybox>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="wallpaper_url">
                          Wallpaper Image
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id="wallpaper_url"
                          name="wallpaper_url"
                          className="my-2"
                          accept="image/*"
                          onChange={(e) =>
                            setWallpaperFile(e.target.files[0] || null)
                          }
                        />
                        <Fancybox>
                          {user.wallpaper_url ? (
                            <a
                              data-fancybox="wallpaper-image"
                              href={user.wallpaper_url}
                            >
                              <img
                                src={user.wallpaper_url}
                                className="hv-40 bg-light"
                                alt="Wallpaper"
                              />
                            </a>
                          ) : (
                            ""
                          )}
                        </Fancybox>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="wallpaper_url_ipad">
                          Wallpaper Image (iPad)
                        </Form.Label>
                        <Form.Control
                          type="file"
                          id="wallpaper_url_ipad"
                          name="wallpaper_url_ipad"
                          className="my-2"
                          accept="image/*"
                          onChange={(e) =>
                            setWallpaperIpadFile(e.target.files[0] || null)
                          }
                        />
                        <Fancybox>
                          {user.wallpaper_url_ipad ? (
                            <a
                              data-fancybox="wallpaper-ipad-image"
                              href={user.wallpaper_url_ipad}
                            >
                              <img
                                src={user.wallpaper_url_ipad}
                                className="hv-40 bg-light"
                                alt="Wallpaper iPad"
                              />
                            </a>
                          ) : (
                            ""
                          )}
                        </Fancybox>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <ImageInput
                        label="Full Image (iPhone)"
                        accept="image/*"
                        existingUrl={user.full_image || null}
                        onChange={(file) => setFullImageFile(file)}
                      />
                    </Col>
                    <Col lg={3} sm={12}>
                      <ImageInput
                        label="Full Image (iPad)"
                        accept="image/*"
                        existingUrl={user.full_image_ipad || null}
                        onChange={(file) => setFullImageIpadFile(file)}
                      />
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="resolution">Resolution</Form.Label>
                        <Form.Control
                          type="text"
                          id="resolution"
                          className="my-2"
                          name="resolution"
                          placeholder="e.g. 1080x1920"
                          value={user.resolution}
                          onChange={onInputChange}
                          onBlur={() => markTouched("resolution")}
                          isInvalid={!!showError("resolution")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.resolution}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col lg={3} sm={12}>
                      <Form.Group>
                        <Form.Label htmlFor="resolution_ipad">
                          Resolution (iPad)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="resolution_ipad"
                          className="my-2"
                          name="resolution_ipad"
                          placeholder="e.g. 2048x2732"
                          value={user.resolution_ipad}
                          onChange={onInputChange}
                          onBlur={() => markTouched("resolution_ipad")}
                          isInvalid={!!showError("resolution_ipad")}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.resolution_ipad}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col lg={6} sm={12}>
                      <TagsInput
                        id="tags"
                        label="Tags"
                        tags={user.tags}
                        onChange={(next) =>
                          setUser((prev) => ({ ...prev, tags: next }))
                        }
                      />
                    </Col>
                    <Col lg={3} sm="12" className="mb-2">
                      <Form.Label className="d-block mb-2">Status</Form.Label>
                      <Switch
                        onChange={(checked) => setStatus(checked)}
                        checked={status === true}
                        offColor="#C8C8C8"
                        onColor="#0093ed"
                        height={30}
                        width={70}
                        className="react-switch"
                        uncheckedIcon={
                          <div className="react-switch-off">OFF</div>
                        }
                        checkedIcon={<div className="react-switch-on">ON</div>}
                      />
                    </Col>
                    <Col lg={3} sm="12" className="mb-2">
                      <Form.Label className="d-block mb-2">
                        Is Premium
                      </Form.Label>
                      <Switch
                        onChange={(checked) => setPremium(checked)}
                        checked={premium === true}
                        offColor="#C8C8C8"
                        onColor="#0093ed"
                        height={30}
                        width={70}
                        className="react-switch"
                        uncheckedIcon={
                          <div className="react-switch-off">NO</div>
                        }
                        checkedIcon={<div className="react-switch-on">YES</div>}
                      />
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer className="text-end">
                  <Button
                    variant="primary"
                    className="me-3"
                    onClick={handleSubmission}
                    disabled={loading}
                  >
                    Update
                  </Button>
                  <Link to="/wallpaper">
                    <Button variant="secondary">Cancle</Button>
                  </Link>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </Layout>
  );
};

export default WallpaperEdit;
